/*
 * Landing-page video autoplay (Best Luggage). Same behaviour as the lp-006-3 video loader:
 *  - videos are forced muted + inline (needed for autoplay on iOS, Android and in-app browsers
 *    such as Instagram),
 *  - each video plays when it scrolls into view and pauses when it leaves,
 *  - if the browser blocks playback (e.g. iPhone Low Power Mode) the section's play button / overlay
 *    stays visible so the visitor can start it with a tap.
 * Extras: if the visitor unmutes a video it is not muted again on the next scroll-in, and the
 * wrapper gets the `is-playing` class while the video plays (used by existing section CSS).
 * Opt-in markup:
 *   <video data-lp-autoplay-video ...>          video to autoplay
 *   [data-lp-video-wrap]                         optional wrapper (defaults to the video's parent)
 *   [data-lp-play-button] inside the wrapper     optional button shown only while paused/blocked;
 *                                                tapping it plays the video (with native controls)
 */
(() => {
  if (window.LpVideoAutoplay) {
    window.LpVideoAutoplay.scan(document);
    return;
  }

  const selector = 'video[data-lp-autoplay-video]';
  const registered = new WeakSet();
  const visible = new WeakMap();
  const retryTimers = new WeakMap();
  let observer = null;

  const wrapFor = (video) => video.closest('[data-lp-video-wrap]') || video.parentElement;
  const buttonFor = (video) => {
    const wrap = wrapFor(video);
    return wrap ? wrap.querySelector('[data-lp-play-button]') : null;
  };
  const showButton = (video, show) => {
    const button = buttonFor(video);
    if (button) button.style.display = show ? '' : 'none';
  };
  const setPlaying = (video, playing) => {
    const wrap = wrapFor(video);
    if (wrap) wrap.classList.toggle('is-playing', playing);
  };
  const setState = (video, state) => {
    video.dataset.playbackState = state;
  };
  const clearRetry = (video) => {
    const timer = retryTimers.get(video);
    if (timer) window.clearTimeout(timer);
    retryTimers.delete(video);
  };
  const showRecovery = (video, state) => {
    clearRetry(video);
    setState(video, state);
    setPlaying(video, false);
    showButton(video, true);
  };
  const ensureSource = (video) => {
    const lazySource = video.querySelector('source[data-src]');
    if (lazySource) {
      if (!lazySource.getAttribute('src')) {
        lazySource.setAttribute('src', lazySource.dataset.src || '');
        video.load();
      }
      return Boolean(lazySource.getAttribute('src'));
    }
    if (video.querySelector('source[src]') || video.getAttribute('src')) return true;
    if (video.dataset.src) {
      video.setAttribute('src', video.dataset.src);
      return true;
    }
    showRecovery(video, 'missing-source');
    return false;
  };
  const scheduleRecovery = (video, state = 'stalled') => {
    clearRetry(video);
    const timer = window.setTimeout(() => {
      if (visible.get(video) && (video.paused || video.readyState < 2)) showRecovery(video, state);
    }, 2800);
    retryTimers.set(video, timer);
  };
  const forceMuted = (video) => {
    video.muted = true;
    video.defaultMuted = true;
  };
  const attemptPlay = async (video) => {
    if (!visible.get(video)) return;
    if (video.dataset.lpUserUnmuted !== 'true') forceMuted(video);
    video.playsInline = true;
    if (!ensureSource(video)) return;
    if (!video.paused) return;
    setState(video, 'loading');
    scheduleRecovery(video);
    try {
      await video.play();
      clearRetry(video);
      setState(video, 'playing');
    } catch {
      if (!video.muted) {
        // Unmuted playback blocked: fall back to muted autoplay.
        forceMuted(video);
        try {
          await video.play();
          clearRetry(video);
          setState(video, 'playing');
          return;
        } catch { /* fall through */ }
      }
      showRecovery(video, 'play-blocked');
    }
  };
  const register = (video) => {
    if (registered.has(video)) return;
    registered.add(video);
    visible.set(video, false);
    forceMuted(video);
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.removeAttribute('autoplay');
    setState(video, 'idle');
    video.addEventListener('playing', () => {
      clearRetry(video);
      setState(video, 'playing');
      setPlaying(video, true);
      showButton(video, false);
    });
    video.addEventListener('pause', () => {
      setPlaying(video, false);
      if (visible.get(video) && !video.ended) showButton(video, true);
    });
    video.addEventListener('volumechange', () => {
      if (!video.muted && video.dataset.playbackState === 'playing') video.dataset.lpUserUnmuted = 'true';
      if (video.muted) delete video.dataset.lpUserUnmuted;
    });
    video.addEventListener('loadeddata', () => {
      if (visible.get(video)) attemptPlay(video);
    });
    video.addEventListener('canplay', () => {
      if (visible.get(video) && video.paused) attemptPlay(video);
    });
    video.addEventListener('waiting', () => scheduleRecovery(video, 'buffering'));
    video.addEventListener('stalled', () => scheduleRecovery(video, 'stalled'));
    video.addEventListener('error', () => showRecovery(video, 'media-error'));
    const button = buttonFor(video);
    if (button && !button.dataset.lpBound) {
      button.dataset.lpBound = 'true';
      button.addEventListener('click', () => {
        // User tap: play this video right away (inside the gesture) and show native controls.
        video.controls = true;
        if (!ensureSource(video)) return;
        video.play().catch(() => {
          forceMuted(video);
          video.play().catch(() => showRecovery(video, 'play-blocked'));
        });
      });
    }
    if (observer) observer.observe(video);
    else {
      visible.set(video, true);
      attemptPlay(video);
    }
  };
  const scan = (root = document) => {
    if (root.matches && root.matches(selector)) register(root);
    if (root.querySelectorAll) root.querySelectorAll(selector).forEach(register);
  };

  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        visible.set(entry.target, entry.isIntersecting);
        if (entry.isIntersecting) attemptPlay(entry.target);
        else if (!entry.target.paused) entry.target.pause();
      });
    }, { rootMargin: '240px 0px', threshold: 0.01 });
  }

  window.LpVideoAutoplay = { scan, attemptPlay };
  scan(document);
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1) scan(node);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('pageshow', () => scan(document));
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) document.querySelectorAll(selector).forEach((video) => attemptPlay(video));
  });
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
